import { ShieldCheck, AlertTriangle } from "lucide-react";

/**
 * GuardianDetailsPanel - staff-facing display of parent/guardian consent on a submission.
 * Rendered inside the submission viewers so it is also captured in the PDF export.
 */
export default function GuardianDetailsPanel({ submission }) {
  if (!submission) return null;

  const guardian = submission.guardian_consent;
  const required = Boolean(submission.guardian_required || submission.is_minor_submission);

  if (!guardian && !required) return null;

  if (!guardian) {
    return (
      <div
        className="mt-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 flex items-start gap-2"
        data-testid="guardian-details-missing"
      >
        <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-amber-700 leading-relaxed">
          Guardian consent is required for this patient but no guardian details were recorded. Reject this
          form and request a resubmission.
        </p>
      </div>
    );
  }

  return (
    <div className="mt-6 pt-4 border-t border-[#1A1B1A]/[0.06]" data-testid="guardian-details-panel">
      <h4 className="text-xs font-semibold text-[#757675] uppercase tracking-wider mb-3">
        Parent or guardian consent
      </h4>
      <div className="rounded-xl border border-[#205B5A]/20 bg-[#205B5A]/[0.03] p-4 space-y-3">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-3.5 h-3.5 text-[#205B5A]" />
          <span className="text-xs text-[#205B5A] font-medium">
            Consent given by a parent or guardian on behalf of a patient under 18
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-[#757675] mb-0.5">Guardian name</p>
            <p className="text-sm text-[#1A1B1A]" data-testid="guardian-detail-name">
              {guardian.name || "Not provided"}
            </p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-[#757675] mb-0.5">Relationship</p>
            <p className="text-sm text-[#1A1B1A]" data-testid="guardian-detail-relationship">
              {guardian.relationship_display || guardian.relationship || "Not provided"}
            </p>
          </div>
        </div>

        <div>
          <p className="text-[10px] uppercase tracking-wider text-[#757675] mb-1">Guardian signature</p>
          <div className="border border-[#1A1B1A]/10 rounded-lg bg-white p-3">
            {guardian.signature_data ? (
              <img
                src={guardian.signature_data}
                alt="Guardian signature"
                className="max-h-24 w-auto"
                data-testid="guardian-drawn-signature"
              />
            ) : guardian.typed_signature ? (
              <p
                className="text-xl text-[#1A1B1A] italic"
                style={{ fontFamily: "'Brush Script MT', 'Segoe Script', cursive" }}
                data-testid="guardian-typed-signature"
              >
                {guardian.typed_signature}
              </p>
            ) : (
              <p className="text-sm text-[#757675] italic">No signature provided</p>
            )}
          </div>
        </div>

        {submission.patient_age_at_submission != null && (
          <p className="text-[10px] text-[#757675]" data-testid="guardian-detail-age">
            Patient age at submission: {submission.patient_age_at_submission}
          </p>
        )}
      </div>
    </div>
  );
}
